import type { Filter, FilterOption, MarketLabelOverrides } from '@interfaces/filters/catalog';
import type { SegmentFilterView } from '@interfaces/segments/domain';

/**
 * A market's relabel wins only when EVERY selected market renames the entity
 * to the same string; any disagreement, or a market without an override,
 * falls back to the standard label. Mirrors the server's echo rule, so a
 * label shown here matches the one a multi-market response carries.
 */
export function resolveMarketLabel(
  fallback: string,
  overrides: MarketLabelOverrides | null | undefined,
  marketIds: number[],
): string {
  if (!overrides || marketIds.length === 0) return fallback;
  const first = overrides[String(marketIds[0])];
  if (first === undefined) return fallback;
  return marketIds.every((m) => overrides[String(m)] === first) ? first : fallback;
}

function withOptionLabel(option: FilterOption, marketIds: number[]): FilterOption {
  const displayLabel = resolveMarketLabel(option.displayLabel, option.labelOverrides, marketIds);
  return displayLabel === option.displayLabel ? option : { ...option, displayLabel };
}

/**
 * The filter with `displayLabel` (its own and every option's) resolved for
 * `marketIds`. Returns the same object when nothing is relabelled.
 */
export function withMarketLabels(filter: Filter, marketIds: number[]): Filter {
  const displayLabel = resolveMarketLabel(filter.displayLabel, filter.labelOverrides, marketIds);
  const options = filter.options.map((o) => withOptionLabel(o, marketIds));
  const optionsChanged = options.some((o, i) => o !== filter.options[i]);
  if (displayLabel === filter.displayLabel && !optionsChanged) return filter;
  return { ...filter, displayLabel, options: optionsChanged ? options : filter.options };
}

/**
 * Segment filter views carry raw registry names. Swap in the catalog label
 * (already resolved for the selected markets) by code; a filter or option
 * missing from the catalog, e.g. inaccessible or not offered here, keeps the
 * name the segments API sent.
 */
export function withCatalogLabels(
  views: SegmentFilterView[],
  filtersByCode: Map<string, Filter>,
): SegmentFilterView[] {
  return views.map((view) => {
    const filter = filtersByCode.get(view.code);
    if (!filter) return view;
    const name = filter.displayLabel || view.name;
    if (!('options' in view)) return { ...view, name };
    return {
      ...view,
      name,
      options: view.options.map((o) => {
        const label = filter.options.find((fo) => fo.code === o.code)?.displayLabel;
        return label ? { ...o, name: label } : o;
      }),
    };
  });
}
